import { Alert } from "@mui/material";
import { CustomerLink } from "portal/components/customers/CustomerLink";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { portalEndpoints } from "portal/state/portalApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { Trans, useTranslation } from "react-i18next";
import { useCustomerScopeFilter } from "portal/components/customers/useCustomerScopeFilter";
import React, { FC } from "react";

interface ScopedCustomerMissingFeatureFlagAlertProps {
  flag: FeatureFlag;
  explicitCustomerUuid?: string;
}

/** Support-only: scoped customer is missing a feature flag the current page needs. Pulls the customer from the customer scoping param unless explicit customer is provided as an override */
export const ScopedCustomerMissingFeatureFlagAlert: FC<
  ScopedCustomerMissingFeatureFlagAlertProps
> = ({ flag, explicitCustomerUuid }) => {
  const { t } = useTranslation();
  const { canSeeOtherCustomers, customerUuid: scopedCustomerUuid } =
    useCustomerScopeFilter();
  const customerUuid = explicitCustomerUuid ?? scopedCustomerUuid;
  const { isEnabled, isLoading } = useFeatureFlagForCustomer(
    flag,
    customerUuid
  );
  const { data: scopedCustomer } = portalEndpoints.getCustomer.useQuery(
    canSeeOtherCustomers && customerUuid ? { customerUuid } : skipToken
  );

  if (
    !canSeeOtherCustomers ||
    !customerUuid ||
    !scopedCustomer ||
    isLoading ||
    Boolean(isEnabled)
  ) {
    return;
  }

  return (
    <Alert severity="warning" className="mb-8 shrink-0">
      <Trans
        i18nKey="components.customers.warnings.featureFlagOff"
        t={t}
        values={{
          name: scopedCustomer.name,
          flagName: t(`models.customers.fields.featureFlags.${flag}.name`),
        }}
        components={{
          customersLink: <CustomerLink customerUuid={customerUuid} />,
        }}
      />
    </Alert>
  );
};
